import { withTrailingSlash } from '../../utils/trailingSlash';
import React, { useState, useEffect, useContext, Fragment } from 'react';
//@ts-ignore
import * as styles from './navigation.module.css';
import {
    InstantSearch,
    connectInfiniteHits,
    Configure,
    connectSearchBox,
} from 'react-instantsearch-dom';
//@ts-ignore
import { useBreakpoint } from 'gatsby-plugin-breakpoints';
import SvgArrowLgLeft from '../Global/ArrowLgButton/SvgArrowLgLeft';
import { AuthorStyles } from './index';
import { KontentMedia, KontentRichText } from '../../../types/KontentProps';
import ShowMoreArrow from '../Global/ShowMoreArrow';
import ArrowLgButton from '../Global/ArrowLgButton';
import { searchClient } from '../Global/DataUtils/algolia';
import {
    PageMetadataContext,
    PageMetaType,
} from '../../templates/PageMetaDataContext';
import parse from 'html-react-parser';

import { supportedRegionCodes, isRootLang } from '../../global/countries';
import {
    getAlgoliaIndexPrefix,
    getAlgoliaLangSuffix,
} from '../../global/utility';
import CloseButton from '../Global/CloseButton';
import {
    optimizeKontentImageUrl,
    buildKontentSrcSet,
    getDisplayDimensions,
} from '../Global/DataUtils';
import LazyImage from '../Global/LazyImage';

export interface SiteSearchProps {
    searchTerm?: string;
    setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
    toggleNavbar: () => void;
    goBackAction: () => void;
    authorStyles: AuthorStyles;
    backLabel?: string;
    preferredLanguage: string;
    footer?: JSX.Element;
    ariaLabel: string;
    keywordHintText: string;
    searchPanelTitle: string;
    desktopSearchCtaLabel: string;
    noResultHeadline: string;
    noResultCopy: string;
}

interface InfiniteHitsProps {
    hits: Hit[];
    hasPrevious: boolean;
    hasMore: boolean;
    refinePrevious: () => any;
    refineNext: () => any;
    authorStyles: AuthorStyles;
    ctaLabel?: string;
    urlPrefix: string;
    noResultHeadline: string;
    noResultCopy: string;
}

interface SearchBoxProps {
    currentRefinement: string;
    refine: (event: any) => any;
    isSearchStalled: boolean;
    onChange?: (event: any) => any;
    ariaLabel?: string;
    keywordHintText?: string;
}

interface Hit {
    objectID: string;
    slug: string;
    url: string;
    display_page_name: string;
    seo_page_title: string;
    page_search_summary: string;
    page_search_image: KontentMedia['value'];
    page_type: string;
    blog_topic: string;
    preferred_language: string;
    answer?: KontentRichText;
}
const parseHtml = (htmlAsString: string) => {
    let html = htmlAsString;
    html = html.replace(/&lt;/g, '<').replace(/&gt;/g, '>');
    html = html.replace(/\n/g, '');
    const faq = parse(html);
    return faq;
};

const stripCountrySlugFromUrl = (url: string) => {
    const pathSegments = url?.split('/');
    const countrySlug = pathSegments[1];

    return (supportedRegionCodes as readonly string[]).includes(countrySlug)
        ? '/' + pathSegments.slice(2).join('/')
        : url;
};

const resolveHitUrlPrefix = (
    hit: Hit,
    urlPrefix: string,
    pageMetadata: PageMetaType
) => {
    if (
        hit.slug === 'empower' &&
        hit.url &&
        pageMetadata?.preferredLanguage &&
        !isRootLang(pageMetadata?.preferredLanguage)
    ) {
        return '/' + pageMetadata.preferredLanguage.toLowerCase();
    }

    return urlPrefix;
};

export const SiteSearch: React.FC<SiteSearchProps> = ({
    searchTerm,
    setSearchTerm,
    ...props
}) => {
    const pageMetaData = useContext(PageMetadataContext);
    // Prefer the page's actual language from context; fall back to the
    // language passed in via props when the context is not yet populated.
    const preferredLanguage =
        pageMetaData.preferredLanguage || props.preferredLanguage;
    const urlPrefix =
        props.preferredLanguage == 'default'
            ? ''
            : `/${props.preferredLanguage.toLowerCase()}`;
    const breakpoints = useBreakpoint();
    const algoliaIndexPrefix = getAlgoliaIndexPrefix(
        process.env.GATSBY_ALGOLIA_ENV
    );
    // Algolia index suffixes are case-sensitive (e.g. `_GB-en`, `_AU-en`)
    // while preferredLanguage from context is lowercased — denormalize.
    const indexName = `${algoliaIndexPrefix}MasterPagesAndFAQs_${getAlgoliaLangSuffix(
        preferredLanguage
    )}`;

    const handleSearchBoxChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setSearchTerm(event.currentTarget.value);
    };

    return (
        <InstantSearch
            indexName={indexName}
            searchClient={searchClient}
            refresh={false}
        >
            <Configure hitsPerPage={4} />
            {!breakpoints.xl && (
                <div className={styles.mobileSecondaryNav}>
                    <button
                        onClick={props.goBackAction}
                        className={
                            styles.mobileButtonLink +
                            ' ' +
                            styles.mobileBackLink
                        }
                    >
                        <div className={styles.arrowContainer}>
                            <div className={styles.back}>
                                <SvgArrowLgLeft strokeColor="#505251" />
                            </div>
                        </div>
                        <span className={styles.buttonCopy}>
                            {props.searchPanelTitle}
                        </span>
                    </button>
                </div>
            )}

            {breakpoints.xl && props.searchPanelTitle && (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <div className={styles.searchTitle}>
                        {props.searchPanelTitle}
                    </div>
                    <div className={styles.closeButton}>
                        <CloseButton onClick={props.toggleNavbar} />
                    </div>
                </div>
            )}

            <div className={styles.searchInput}>
                <label htmlFor="search" className="sr-only">
                    {props.ariaLabel}
                </label>
                <CustomSearchBox
                    defaultRefinement={searchTerm}
                    onChange={handleSearchBoxChange}
                    ariaLabel={props.ariaLabel}
                    keywordHintText={props.keywordHintText}
                />
            </div>

            {props.authorStyles.accentColor && breakpoints.xl ? (
                <style>{`.${styles.contentCardLink}:hover div.${styles.cta} { 
                        border-color: ${props.authorStyles.accentColor} !important; 
                        color: ${props.authorStyles.color} !important;
                        background-color: ${props.authorStyles.accentColor} !important;
                    }`}</style>
            ) : null}

            <CustomHits
                authorStyles={props.authorStyles}
                ctaLabel={props.desktopSearchCtaLabel}
                urlPrefix={urlPrefix}
                noResultHeadline={props.noResultHeadline}
                noResultCopy={props.noResultCopy}
            />
            {props.footer}
        </InstantSearch>
    );
};

const InfiniteHits: React.FC<InfiniteHitsProps> = ({
    hits,
    hasMore,
    hasPrevious,
    refineNext,
    refinePrevious,
    authorStyles,
    urlPrefix,
    noResultHeadline,
    noResultCopy,
    ...props
}) => {
    const breakpoints = useBreakpoint();
    const pageMetadata = useContext(PageMetadataContext);
    const [currentHit, setCurrentHit] = useState(
        breakpoints.xl && hits.length - 4
    );

    const [visibleHits, setVisibleHits] = useState(
        breakpoints.xl
            ? hits.length > 4
                ? hits.slice(currentHit, currentHit + 4)
                : hits
            : hits
    );
    useEffect(() => {
        if (!breakpoints.xl) {
            setCurrentHit(0);
            setVisibleHits(hits);
            return;
        }
        const newHit = Math.max(hits.length - 4, 0);
        setCurrentHit(newHit);
        setVisibleHits(hits.slice(newHit, newHit + 4));
    }, [breakpoints, hits]);

    const inlineStyle: React.CSSProperties = {};

    if (authorStyles.color) {
        inlineStyle.color = authorStyles.color;
        inlineStyle.borderColor = authorStyles.color;
    }

    const renderHit = (hit: Hit, idx: number) => {
        const isEmpowerHit = hit.slug === 'empower' && Boolean(hit.url);
        const hitUrl = isEmpowerHit
            ? stripCountrySlugFromUrl(hit.url)
            : hit.url;
        const hitUrlPrefix = resolveHitUrlPrefix(hit, urlPrefix, pageMetadata);

        if (breakpoints.xl) {
            const image = hit?.page_search_image?.[0]?.elements?.file?.value[0];
            return (
                <>
                    {hit?.question ? (
                        <div
                            className={styles.contentCard}
                            style={
                                visibleHits.length < 4 && breakpoints.xl
                                    ? { marginRight: '4rem' }
                                    : {}
                            }
                        >
                            <a
                                href={`${hitUrlPrefix}/customer-service/frequently-asked-questions/?faqID=${hit?.objectID}`}
                                className={styles.contentCardLink}
                            >
                                <div
                                    className={styles.headline}
                                    style={{ color: authorStyles.accentColor }}
                                >
                                    {hit.question}
                                </div>
                                <div className={styles.description}>
                                    {hit?.answer &&
                                    hit?.answer !== '<p><br></p>'
                                        ? parseHtml(
                                              hit.answer.substring(0, 150) +
                                                  '....'
                                          )
                                        : ''}
                                </div>
                                <div className={styles.cta}>{'READ MORE'}</div>
                                {/* <div className={styles.cta}>
                                    {props.ctaLabel}
                                </div> */}
                            </a>
                        </div>
                    ) : (
                        <div
                            className={styles.contentCard}
                            style={
                                visibleHits.length < 4 && breakpoints.xl
                                    ? { marginRight: '4rem' }
                                    : {}
                            }
                        >
                            <a
                                href={withTrailingSlash(
                                    `${hitUrlPrefix}${hitUrl}`
                                )}
                                target="_self"
                                className={styles.contentCardLink}
                            >
                                {image && (
                                    <LazyImage
                                        // Algolia hits may not carry asset
                                        // dims; fall back to 16:9 (CSS width
                                        // 100%, height auto).
                                        {...getDisplayDimensions(
                                            image.width || 16,
                                            image.height || 9,
                                            800
                                        )}
                                        src={optimizeKontentImageUrl(
                                            image.url,
                                            {
                                                w: 800,
                                                fm: 'webp' as const,
                                                q: 95,
                                            }
                                        )}
                                        srcSet={buildKontentSrcSet(
                                            image.url,
                                            800,
                                            {
                                                fm: 'webp' as const,
                                                q: 95,
                                            }
                                        )}
                                        sizes="(max-width: 991px) 100vw, 400px"
                                        alt={image?.description ?? ''}
                                    />
                                )}
                                <div
                                    className={styles.headline}
                                    style={{ color: authorStyles.accentColor }}
                                >
                                    {hit.seo_page_title
                                        ? hit.seo_page_title
                                        : hit.display_page_name}
                                </div>
                                <div className={styles.description}>
                                    {hit.page_search_summary}
                                </div>
                                <div className={styles.cta}>
                                    {props.ctaLabel}
                                </div>
                            </a>
                        </div>
                    )}
                </>
            );
        }

        return (
            <div className={styles.standardLink}>
                <a
                    href={
                        hit?.question
                            ? `${hitUrlPrefix}/customer-service/frequently-asked-questions/?faqID=${hit?.objectID}`
                            : `${hitUrlPrefix}${hitUrl}`
                    }
                    target="_self"
                >
                    {hit?.question
                        ? hit.question
                        : hit.seo_page_title
                        ? hit.seo_page_title
                        : hit.display_page_name}
                </a>
            </div>
        );
    };

    const handlePrevClick = () => {
        const newHit = Math.max(currentHit - 4, 0);
        const newVisibleHits = hits.slice(
            newHit,
            Math.min(newHit + 4, hits.length)
        );

        if (newHit === hits.length && hasPrevious) refinePrevious();

        setVisibleHits(newVisibleHits);
        setCurrentHit(newHit);
    };

    const handleNextClick = () => {
        const newHit = currentHit + 4;
        const newVisibleHits = hits.slice(
            newHit,
            Math.min(newHit + 4, hits.length)
        );
        if (newHit >= hits.length && hasMore) refineNext();
        setVisibleHits(newVisibleHits);
        setCurrentHit(newHit);
    };

    return (
        <div className={styles.searchResults}>
            <div
                key={`group-${currentHit}`}
                className={styles.group + ' ' + styles.searchGroup}
                style={
                    visibleHits.length < 4 && breakpoints.xl
                        ? { justifyContent: 'flex-start' }
                        : {}
                }
            >
                {visibleHits.map((hit: Hit, idx: number) => (
                    <Fragment key={hit?.objectID}>
                        {renderHit(hit, idx)}
                    </Fragment>
                ))}
                {!hits.length && (
                    <div className={styles.noResultsContainer}>
                        <h3>{noResultHeadline}</h3>
                        <p>{noResultCopy}</p>
                    </div>
                )}

                {hasMore && !breakpoints.xl && (
                    <ShowMoreArrow
                        btnText=""
                        handleClick={refineNext}
                        strokeColor="#505251"
                        transform="0"
                    />
                )}
            </div>
            {currentHit > 0 && breakpoints.xl && (
                <ArrowLgButton
                    strokeColor={authorStyles.accentColor}
                    direction="left"
                    handleClick={handlePrevClick}
                    className={styles.prev}
                />
            )}

            {(hasMore || currentHit < hits.length - 4) && breakpoints.xl && (
                <ArrowLgButton
                    strokeColor={authorStyles.accentColor}
                    direction="right"
                    handleClick={handleNextClick}
                    className={styles.next}
                />
            )}
        </div>
    );
};

const CustomHits = connectInfiniteHits(InfiniteHits);

const SearchBox: React.FC<SearchBoxProps> = ({
    currentRefinement,
    refine,
    onChange,
    ariaLabel,
    keywordHintText,
}) => {
    return (
        <input
            type="search"
            value={currentRefinement}
            onChange={(event) => {
                if (onChange) {
                    onChange(event);
                }
                refine(event.currentTarget.value);
            }}
            aria-label={ariaLabel || 'Search'}
            name="search"
            placeholder={keywordHintText || ''}
        />
    );
};

const CustomSearchBox = connectSearchBox(SearchBox);
